import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { formattedError } from '@helpers/functions';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiService } from '../../_services/api-service';
import { PortalService } from '../../_services/portal.service';
import { TokenService } from '../../_services/token.service';
import { DadosColaborador, DataColab, User } from '../../models';
import { AlertService } from '../../shared/alert/alert.service';
import { DadosLogin } from '../interfaces/dadoslogin.interface';
import { PermissionService } from './permission.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private url = environment.apiUrl;
    private jwtHelper = new JwtHelperService();
    private cache: Observable<DadosLogin>;
    private tokenHash: string | null = null;

    current_user: DadosColaborador | undefined;
    dados_login: DadosLogin | undefined;

    get isLogged() {
        try {
            return !this.jwtHelper.isTokenExpired(this.tokenService.token);
        } catch {
            return false;
        }
    }

    constructor(
        private alertService: AlertService,
        private apiService: ApiService,
        private http: HttpClient,
        private permissionService: PermissionService,
        private portalService: PortalService,
        private router: Router,
        private toastrService: ToastrService,
        private tokenService: TokenService
    ) {}

    check() {
        return this.http.post(`${this.url}check`, {});
    }

    alterarSenha(cpf: string) {
        const formData = new FormData();
        formData.append('cpf', cpf);
        return this.http.post(
            `${this.url}colaboradores/esqueci_senha/`,
            formData
        );
    }

    login(user: User) {
        return this.http.post<any>(`${this.url}api-token-auth/`, user).pipe(
            tap(res => {
                if (res.token) {
                    const expiration = this.jwtHelper
                        .getTokenExpirationDate(res.token)
                        .getTime();
                    this.tokenService.store(res.token, expiration);
                    this.limparCache();
                    this.getDadosLogin(true);
                }
            })
        );
    }

    refresh_token() {
        return this.http
            .post<any>(`${this.url}refresh-token/`, {
                token: this.tokenService.token,
            })
            .pipe(
                tap(res => {
                    if (res.token) {
                        const expiration = this.jwtHelper
                            .getTokenExpirationDate(res.token)
                            .getTime();
                        this.tokenService.store(res.token, expiration);
                        this.limparCache();
                    }
                })
            );
    }

    getDadosLogin(reload = false): Observable<DadosLogin> {
        const currentToken = this.tokenService.token;
        const hash = currentToken ? btoa(currentToken).slice(-10) : null;

        if (this.tokenHash !== hash) {
            this.limparCache();
            this.tokenHash = hash;
        }

        if (reload || !this.cache) {
            this.cache = this.http
                .get<DadosLogin>(`${this.url}colaboradores/dadoslogin/teste/`)
                .pipe(
                    tap(dados => {
                        this.dados_login = dados;
                        this.portalService.setPortais(dados.portais);
                        this.permissionService.setUserPermissions(dados);
                    }),
                    catchError(err => {
                        this.toastrService.error('Erro na autenticação');
                        this.alertService.error(formattedError(err), true);
                        this.logout();
                        return throwError(
                            () => new Error('Erro nos dados de login')
                        );
                    }),
                    shareReplay(1)
                );
        }
        return this.cache;
    }

    /** @deprecated */
    dadosLogin(reload = false): Observable<DadosLogin> {
        return this.getDadosLogin(reload);
    }

    getCurrentColaborador() {
        return this.dados_login
            ? of(this.dados_login.dados)
            : this.http
                  .get<DataColab>(`${this.url}colaboradoresdadosbasicos/`)
                  .pipe(map(res => res[0]));
    }

    updatePassword(user) {
        return this.http
            .put(
                `${this.url}usercrud/${this.current_user.dados_basicos.id_user}/`,
                user
            )
            .pipe(tap(() => this.toastrService.success('Senha atualizada')));
    }

    private limparCache() {
        this.cache = null;
        this.tokenHash = null;
        this.dados_login = undefined;
        this.permissionService.clearPermissions();
    }

    async logout() {
        if (this.tokenService.token) {
            this.http
                .post(`${this.url}api-token-auth/logout/`, {
                    token: this.tokenService.token,
                })
                .subscribe();
        }

        this.tokenService.clear();
        this.dados_login = undefined;
        this.current_user = undefined;
        this.apiService.clearCurrent();
        this.permissionService.clearPermissions();
        this.limparCache();

        await this.router.navigate(['/login'], { replaceUrl: true });
    }
}
